import { WriteContractParameters } from '@wagmi/core';
import { useMemo } from 'react';
import { useConnection } from 'wagmi';

import { MZUSD_DECIMALS, USDC_DECIMALS } from '@/app/shared/constants/common';
import { VaultTrancheId } from '@/app/shared/constants/vaults';
import { getTrancheAddressById } from '@/app/shared/constants/web3/chainContracts';
import { ABI } from '@/app/shared/constants/web3/constants';
import { useChainContracts } from '@/app/shared/hooks/blockchain/useChainContracts';
import { useSendBatchTransactions } from '@/app/shared/hooks/blockchain/useSendBatchTransactions/useSendBatchTransactions';
import { useDebounce } from '@/app/shared/hooks/useDebounce';
import { makeApproveTx } from '@/app/shared/lib/blockchain/makeApproveTx';
import { safeParseUnits } from '@/app/shared/lib/safeParseUnits';

interface UseVaultDepositArgs {
  amount: string;
  trancheId: VaultTrancheId;
  vaultId?: string;
  enabled?: boolean;
}

export const useVaultDeposit = ({ amount, trancheId, vaultId, enabled = true }: UseVaultDepositArgs) => {
  const { address, isConnected } = useConnection();
  const contracts = useChainContracts(vaultId);
  const debouncedAmount = useDebounce(amount, 500);

  const amountUsdc = safeParseUnits(debouncedAmount, USDC_DECIMALS);
  const amountMzusd = safeParseUnits(debouncedAmount, MZUSD_DECIMALS);
  const trancheAddress = contracts ? getTrancheAddressById(contracts, trancheId) : undefined;

  const transactions = useMemo((): WriteContractParameters[] => {
    if (!enabled || !contracts || !address || !amountUsdc || !amountMzusd || !trancheAddress) return [];

    return [
      makeApproveTx({
        token: contracts.ASSET,
        spender: contracts.MZUSD,
        amount: amountUsdc,
      }),
      {
        address: contracts.MZUSD,
        abi: ABI.MZUSD,
        functionName: 'deposit',
        args: [amountUsdc],
      },
      makeApproveTx({
        token: contracts.MZUSD,
        spender: trancheAddress,
        amount: amountMzusd,
      }),
      {
        address: trancheAddress,
        abi: ABI.TRANCHE,
        functionName: 'deposit',
        args: [amountMzusd, address],
      },
    ];
  }, [address, amountMzusd, amountUsdc, contracts, enabled, trancheAddress]);

  const isReady = enabled && isConnected && !!address && transactions.length === 4;

  const batch = useSendBatchTransactions({
    transactions,
    enabled: isReady,
  });

  return {
    ...batch,
    amountUsdc,
    amountMzusd,
    trancheAddress,
    contracts,
    debouncedAmount,
    isReady,
  };
};
